/** @jsxImportSource @gongxigames/framework/core/fragments/BuilderY */
/** @jsxRuntime automatic */

import {FC,
    $Container,
    ContainerProps,
    CanBeSignal,
    EventCallbacks,
    getProp,
    SignalEventsHandler,
    SignalPropsHandler
} from '@gongxigames/framework/core/fragments/BuilderY';
import {
    TextInput,
    TextInputStyle,
    TextInputBox
} from '@gongxigames/framework/core/fragments/display';
import {CoreDI} from '@gongxigames/framework/core/configs';
import {IBrowser} from '@gongxigames/framework/core/types';
import {Store, Signal} from '@gongxigames/framework/core/fragments/store';
import {inversifyContainer} from '@gongxigames/framework/core/installer';

type InputTextEvents = {
    input?: EventCallbacks<(text: string) => void>;
    keydown?: EventCallbacks<(keycode: number) => void>;
    keyup?: EventCallbacks<(keycode: number) => void>;
    focus?: EventCallbacks<() => void>;
    blur?: EventCallbacks<() => void>;
};

type InputTextBaseProps = {
    textStyle: CanBeSignal<TextInputStyle>;
    boxStyle?: CanBeSignal<TextInputBox>;
    disabledPasteFromClipboard?: boolean;
    text?: CanBeSignal<string>;
    placeholder?: CanBeSignal<string>;
    maxLength?: CanBeSignal<number>;
    restrict?: CanBeSignal<RegExp>;
    inputEvents?: InputTextEvents;
};

export type InputProps = InputTextBaseProps & Omit<ContainerProps, 'instance' | 'ref'>;

let browser: IBrowser;

const eventHandler = new SignalEventsHandler<InputTextEvents>()
    .handleEvents(['input', 'focus', 'blur', 'keydown', 'keyup']);

const propsHandler = new SignalPropsHandler<InputTextBaseProps, TextInput>()
    .propsToHandle(['boxStyle', 'textStyle', 'text', 'placeholder', 'restrict'])
    .setHandlerFor('boxStyle', (val, obj) => obj.setBoxStyle(val))
    .setHandlerFor('textStyle', (styles, obj) => {
        for (const key in styles) {
            if (Object.prototype.hasOwnProperty.call(styles, key)) {
                const param = (styles as any)[key];

                obj.setInputStyle(key as any, param);
            }
        }
    });

function createInstance(props: InputProps) {
    const box = getProp(props.boxStyle);
    const input = getProp(props.textStyle);

    browser ??= inversifyContainer.get<IBrowser>(CoreDI.Browser);

    const instance = new TextInput({
        box,
        input,
        disabledPasteFromClipboard: props.disabledPasteFromClipboard
    }, browser);

    return instance;
}

/**
 * Text input component with some text and input field styling.
 * Used in autoplay inputs
 */
export const $InputText: FC<InputProps> = (props) => {
    const instance: TextInput = (props as any).instance ??= createInstance(props);
    const textIsSignal = Store.isNotReadonlySignal(props.text);
    const unsubscribeProps = propsHandler.apply(props, instance);

    instance.on('destroyed', unsubscribeProps);

    if (textIsSignal) {
        instance.on('input', (val: string) => {
            (props.text as Signal).value = val;
        });
    }

    if (props.inputEvents) {
        const off = eventHandler.apply(props.inputEvents, instance);

        instance.on('destroyed', off);
    }

    instance.on('pointertap', () => {
        instance.focus();
    });

    return $Container(props as ContainerProps);
};